import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import articlesService from '../../services/articlesService';
import engagementService from '../../services/engagementService';
import { useAuth } from '../../contexts/AuthContext';
import AdminArticleMenu from '../../components/ui/AdminArticleMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { toArabicDigits, withArabicDigits } from '../../utils/numbers';

import DiscussionHeader from './components/DiscussionHeader';
import ConversationStream from './components/ConversationStream';
import ContributionInput from './components/ContributionInput';
import ShareButton from '../article-reading/components/ShareButton';

const DiscussionCircle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin } = useAuth();
  const [discussionData, setDiscussionData] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasClapped, setHasClapped] = useState(false);
  const [clapCount, setClapCount] = useState(0);
  const [clapLoading, setClapLoading] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [deletingComment, setDeletingComment] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [bookmarkLoading, setBookmarkLoading] = useState(false);

  // Backend Integration
  useEffect(() => {
    const fetchDiscussion = async () => {
      try {
        const { data } = await articlesService.getArticle(id);

        // Map backend response to component structure
        setDiscussionData({
          id: data.id,
          tag: data.circle?.replace('دائرة ', '') || 'General',
          tagAr: data.circle,
          title: data.title,
          content: data.content || [],
          authorId: data.authorId,
          initiator: {
            id: data.authorId,
            name: data.author,
            avatar: data.authorImage || null,
            avatarAlt: data.authorImageAlt
          },
          startedDate: data.publishedDate
        });
        setIsBookmarked(data.hasUserBookmarked || false);
        setHasClapped(data.hasUserClapped || false);
        setClapCount(data.claps || 0);

      } catch (error) {
        console.error('Failed to fetch discussion:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDiscussion();
  }, [id]);

  // Fetch contributions (comments)
  useEffect(() => {
    const fetchContributions = async () => {
      if (!id) return;
      try {
        const { data } = await engagementService.getComments(id);

        // Transform backend comments to component format
        const transformComment = (comment) => ({
          id: comment.id,
          userId: comment.userId,
          author: comment.userName,
          avatar: comment.userAvatar,
          avatarAlt: comment.userName,
          text: comment.content,
          timestamp: formatDate(comment.createdAt),
          likeCount: comment.likeCount ?? 0,
          hasUserLiked: comment.hasUserLiked ?? false,
          replies: comment.replies?.map(transformComment) || []
        });

        setContributions(data.map(transformComment));
      } catch (error) {
        console.error('Failed to fetch contributions:', error);
      }
    };

    fetchContributions();
  }, [id]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${toArabicDigits(diffMins)} دقيقة`;
    if (diffHours < 24) return `منذ ${toArabicDigits(diffHours)} ساعة`;
    if (diffDays < 30) return `منذ ${toArabicDigits(diffDays)} يوم`;
    return withArabicDigits(date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }));
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">جاري التحميل...</div>;
  if (!discussionData) return <div className="min-h-screen flex items-center justify-center">لم يتم العثور على الحلقة</div>;

  const handleAddContribution = async (text) => {
    if (!text.trim()) return;
    try {
      const { data } = await engagementService.addComment(id, { content: text });

      const newContribution = {
        id: data.id,
        author: data.userName,
        avatar: data.userAvatar,
        avatarAlt: data.userName,
        text: data.content,
        timestamp: 'الآن',
        replies: []
      };

      setContributions(prev => [newContribution, ...prev]);
    } catch (error) {
      console.error('Failed to add contribution:', error);
      // Optional: Show error toast
    }
  };

  const handleReply = async (contributionId, replyText) => {
    if (!replyText.trim()) return;
    try {
      const { data } = await engagementService.addComment(id, {
        content: replyText,
        parentCommentId: contributionId
      });

      const newReply = {
        id: data.id,
        author: data.userName,
        avatar: data.userAvatar,
        avatarAlt: data.userName,
        text: data.content,
        timestamp: 'الآن',
        replies: []
      };

      const addReplyToContribution = (items) => {
        return items.map(item => {
          if (item.id === contributionId) {
            return { ...item, replies: [...(item.replies || []), newReply] };
          }
          if (item.replies?.length > 0) {
            return { ...item, replies: addReplyToContribution(item.replies) };
          }
          return item;
        });
      };

      setContributions(prev => addReplyToContribution(prev));
    } catch (error) {
      console.error('Failed to add reply:', error);
    }
  };

  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    setDeletingComment(true);
    try {
      await engagementService.deleteComment(commentToDelete);
      const removeFromTree = (items) =>
        items
          .filter((item) => item.id !== commentToDelete)
          .map((item) => ({
            ...item,
            replies: item.replies ? removeFromTree(item.replies) : [],
          }));
      setContributions((prev) => removeFromTree(prev));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    } finally {
      setDeletingComment(false);
      setCommentToDelete(null);
    }
  };

  const handleEditComment = async (commentId, content) => {
    try {
      const { data } = await engagementService.updateComment(commentId, content);
      const updateInTree = (items) =>
        items.map((item) => {
          if (item.id === commentId) return { ...item, text: data.content };
          if (item.replies?.length) return { ...item, replies: updateInTree(item.replies) };
          return item;
        });
      setContributions((prev) => updateInTree(prev));
    } catch (err) {
      console.error('Failed to edit comment:', err);
    }
  };

  const handleClap = async () => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    if (clapLoading) return;
    setClapLoading(true);
    try {
      if (hasClapped) {
        await engagementService.removeClap(id);
        setHasClapped(false);
        setClapCount((prev) => Math.max(0, prev - 1));
      } else {
        await engagementService.clap(id);
        setHasClapped(true);
        setClapCount((prev) => prev + 1);
      }
    } catch {
      // Revert on error could be added
    } finally {
      setClapLoading(false);
    }
  };

  const handleLikeComment = async (commentId, hasUserLiked) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }
    const delta = hasUserLiked ? -1 : 1;
    const newLiked = !hasUserLiked;
    const updateInTree = (c) => {
      if (c.id === commentId) {
        return { ...c, likeCount: (c.likeCount ?? 0) + delta, hasUserLiked: newLiked };
      }
      return { ...c, replies: (c.replies ?? []).map(updateInTree) };
    };
    setContributions((prev) => prev.map(updateInTree));
    try {
      if (hasUserLiked) {
        await engagementService.removeCommentLike(commentId);
      } else {
        await engagementService.likeComment(commentId);
      }
    } catch {
      const { data } = await engagementService.getComments(id);
      const transformComment = (comment) => ({
        id: comment.id,
        userId: comment.userId,
        author: comment.userName,
        avatar: comment.userAvatar,
        avatarAlt: comment.userName,
        text: comment.content,
        timestamp: formatDate(comment.createdAt),
        likeCount: comment.likeCount ?? 0,
        hasUserLiked: comment.hasUserLiked ?? false,
        replies: comment.replies?.map(transformComment) || []
      });
      setContributions(data.map(transformComment));
    }
  };

  const handleBookmarkToggle = async () => {
    if (!isAuthenticated || bookmarkLoading) return;
    setBookmarkLoading(true);
    try {
      if (isBookmarked) {
        await engagementService.removeBookmark(id);
        setIsBookmarked(false);
      } else {
        await engagementService.bookmarkArticle(id);
        setIsBookmarked(true);
      }
    } catch (err) {
      console.error('Bookmark failed:', err);
    } finally {
      setBookmarkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#F9F7F2] border-b border-[rgba(197,160,89,0.1)]">
        <div className="max-w-[700px] mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#C5A059] hover:text-[#A68548] transition-colors duration-300"
            aria-label="العودة"
          >
            <Icon name="ArrowRight" size={20} />
          </button>
          <div className="flex items-center gap-3">
            <AdminArticleMenu
              articleId={id}
              onDeleteSuccess={() => navigate('/discussions-feed')}
              compact
            />
            {isAuthenticated && (
              <button
                onClick={handleBookmarkToggle}
                disabled={bookmarkLoading}
                className="flex items-center gap-2 text-[#C5A059] hover:text-[#A68548] transition-colors duration-300"
                aria-label={isBookmarked ? 'إزالة الحفظ' : 'حفظ'}
              >
                <Icon
                  name="Bookmark"
                  size={20}
                  fill={isBookmarked ? '#C5A059' : 'none'}
                  className={bookmarkLoading ? 'opacity-50' : ''}
                />
              </button>
            )}
            <ShareButton
              articleId={id}
              articleTitle={discussionData?.title ?? ''}
              variant="inline"
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-[700px] mx-auto px-6 pt-24 pb-32">
        {/* Header Section */}
        <DiscussionHeader discussion={discussionData} />

        {/* Clap / Like Section */}
        <div className="flex flex-col items-center gap-4 mb-12">
          <button
            onClick={handleClap}
            disabled={clapLoading}
            className="flex items-center gap-3 px-6 py-3 rounded-full transition-all duration-300 hover:shadow-lg disabled:opacity-60"
            style={{
              backgroundColor: hasClapped ? 'rgba(197, 160, 89, 0.2)' : 'rgba(197, 160, 89, 0.1)',
              border: '2px solid #C5A059',
              fontFamily: 'Amiri, serif',
              fontSize: '16px',
              color: '#C5A059',
            }}
          >
            <Icon
              name={hasClapped ? 'HeartOff' : 'Heart'}
              size={22}
              color="#C5A059"
            />
            <span>{hasClapped ? 'إلغاء الإعجاب' : 'أعجبني'}</span>
            <span
              className="mr-1 px-2 py-0.5 rounded-full text-sm"
              style={{ backgroundColor: 'rgba(197, 160, 89, 0.15)' }}
            >
              {toArabicDigits(clapCount)}
            </span>
          </button>
        </div>

        {/* التعليقات Section — same structure as article page */}
        <div
          className="p-8 rounded-2xl mb-8"
          style={{ backgroundColor: 'rgba(197, 160, 89, 0.03)' }}
        >
          <h2
            className="mb-6"
            style={{
              fontFamily: 'Aref Ruqaa, serif',
              fontSize: '28px',
              color: '#2C2B29',
              fontWeight: 700,
            }}
          >
            التعليقات
          </h2>

          {!isAuthenticated && (
            <div
              className="text-center py-6 mb-6 rounded-xl"
              style={{ backgroundColor: 'rgba(197, 160, 89, 0.06)' }}
            >
              <p
                className="mb-3"
                style={{ fontFamily: 'Amiri, serif', fontSize: '16px', color: '#5A5A5A' }}
              >
                سجّل دخولك لإضافة تعليق
              </p>
              <button
                onClick={() => navigate('/signup')}
                className="px-5 py-2 rounded-full text-sm transition-all duration-300"
                style={{
                  fontFamily: 'Amiri, serif',
                  backgroundColor: '#C5A059',
                  color: 'white',
                  fontWeight: 600,
                }}
              >
                تسجيل الدخول
              </button>
            </div>
          )}

          {/* Comments List */}
          <ConversationStream
            contributions={contributions}
            onReply={handleReply}
            onLikeComment={handleLikeComment}
            isAdmin={isAdmin}
            currentUserId={user?.id}
            isAuthenticated={isAuthenticated}
            onDeleteComment={(commentId) => setCommentToDelete(commentId)}
            onEditComment={handleEditComment}
            onUserClick={(userId) => navigate(`/user/${userId}`)}
            onSignupRedirect={() => navigate('/signup')}
          />
        </div>
      </main>

      {/* Sticky Input — only when authenticated */}
      {isAuthenticated && (
        <ContributionInput onSubmit={handleAddContribution} />
      )}

      {/* Comment Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!commentToDelete}
        onConfirm={handleDeleteComment}
        onCancel={() => setCommentToDelete(null)}
        title="حذف التعليق"
        message="هل أنت متأكد من حذف هذا التعليق؟"
        loading={deletingComment}
      />
    </div>
  );
};

export default DiscussionCircle;